import { Feature, Geometry } from "geojson";

export type BBox2D = [number, number, number, number];

/**
 * To greatly simplify the logic methods here don't support bboxes that cross the antimeridian
 * All country that cross (https://en.wikipedia.org/wiki/180th_meridian) have been modified to not cross
 * so we shouldn't have a need for it.
 */
export const BBox2D = {
    isPoint: (bbox: BBox2D | undefined) => {
        return !!bbox
            && bbox.length === 4
            && bbox[0] === bbox[2]
            && bbox[1] === bbox[3];
    },

    crossesAntimeridian: (bbox: BBox2D | undefined) => {
        return !!bbox && bbox[0] > bbox[2];
    },

    antimeridianSplit: (bbox: BBox2D): BBox2D[] => {
        if (BBox2D.crossesAntimeridian(bbox)) {
            const [w, s, e, n] = bbox;
            return [[w, s, 180, n], [-180, s, e, n]];
        } else {
            return [bbox];
        }
    },

    /**
     * @returns the area of the BBox in km2
     */
    area: (bbox: BBox2D | undefined) => {
        if (!bbox || BBox2D.isPoint(bbox)) {
            return 0;
        }

        const [w, s, e, n] = bbox;

        return Math.pow(6371, 2)
            * Math.PI
            * Math.abs(Math.sin(deg2Rad(n)) - Math.sin(deg2Rad(s)))
            * Math.abs(w - e) / 180;
    },

    /**
     * @returns the GeoJson Point point at the center of the BBox `[ lon, lat ]` format.
     */
    centroid: (bbox: BBox2D): [number, number] => {
        return [avg(bbox[0], bbox[2]), avg(bbox[1], bbox[3])];
    },

    intersection: (...bboxes: (BBox2D | undefined)[]): BBox2D | undefined => {
        let result: BBox2D | undefined = undefined;

        bboxes.every(value => {
            if (!value) {
                return true;
            }

            if (!result) {
                result = value;
                return true;
            }

            let [rW, rS, rE, rN] = result;
            let [vW, vS, vE, vN] = value;

            if (rE < vW || rN < vS || rW > vE || rS > vN) {
                result = undefined;
                return false;
            }

            result = [
                Math.max(rW, vW),
                Math.max(rS, vS),
                Math.min(rE, vE),
                Math.min(rN, vN)
            ]
            return true;
        });

        return result;
    },

    union: (...bboxes: BBox2D[]): BBox2D | undefined => {
        if (bboxes.length === 0) {
            return undefined;
        }

        return [
            Math.min(...bboxes.map(x => x[0])),
            Math.min(...bboxes.map(x => x[1])),
            Math.max(...bboxes.map(x => x[2])),
            Math.max(...bboxes.map(x => x[3]))
        ]
    },

    max: (...bboxes: BBox2D[]): BBox2D | undefined => {
        return bboxes.sort((a, b) => BBox2D.area(b) - BBox2D.area(a))[0];
    },

    mercatorProjectionCenter: (bbox: BBox2D): [number, number] => {
        const [minX, minY, maxX, maxY] = bbox;
        const ry1 = Math.log((Math.sin(deg2Rad(minY)) + 1) / Math.cos(deg2Rad(minY)));
        const ry2 = Math.log((Math.sin(deg2Rad(maxY)) + 1) / Math.cos(deg2Rad(maxY)));
        const cY = rad2Deg(Math.atan(Math.sinh(avg(ry1, ry2))));
        const cX = avg(minX, maxX);
        return [cX, cY];
    },

    /**
     * OSM Zoom Level Documentation: https://wiki.openstreetmap.org/wiki/Zoom_levels
     * Solution: https://gis.stackexchange.com/questions/19632/how-to-calculate-the-optimal-zoom-level-to-display-two-or-more-points-on-a-map
     * @Returns Get the OpenStreetMaps zoom level that fits the bounding box
     */
    osmZoomLevel: (bbox: BBox2D | undefined, viewport: [number, number], paddingFactor: number): number => {
        if (!bbox) return 1;

        const [width, height] = viewport;
        const [minX, minY, maxX, maxY] = bbox;
        const deltaY = Math.abs(maxY - minY);
        const deltaX = Math.abs(maxX - minX);

        if (deltaY === 0 && deltaY === 0) {
            return 15;
        }

        const [_, cY] = BBox2D.mercatorProjectionCenter(bbox);
        const vy0 = Math.log(Math.tan(Math.PI * (0.25 + cY / 360)));
        const vy1 = Math.log(Math.tan(Math.PI * (0.25 + maxY / 360)));
        const zoomFactorPowered = (height / 2) / (40.7436654315252 * (vy1 - vy0));
        const resolutionVertical = 360.0 / (zoomFactorPowered * 256);

        const resolutionHorizontal = deltaX / width;
        const resolution = Math.max(resolutionHorizontal, resolutionVertical) * paddingFactor;

        const zoom = Math.log2(360 / (resolution * 256));
        return Math.ceil(Math.min(18, Math.max(1, zoom)));
    },

    toFeature: (bbox: BBox2D | undefined): Feature | undefined => {
        return bbox ? {
            type: "Feature",
            bbox: bbox,
            geometry: toGeometry(bbox),
            properties: {}
        } : undefined
    }

}

function toGeometry(bbox: BBox2D): Geometry {
    const [w, s, e, n] = bbox;

    if (BBox2D.isPoint(bbox)) {
        return {
            type: "Point",
            coordinates: [e, n]
        }
    } else {
        // The first and last positions are equivalent, and they MUST contain identical values; their representation SHOULD also be identical.
        // a linear ring MUST follow the right-hand rule with respect to the area it bounds, i.e., exterior rings are counterclockwise
        // https://stevage.github.io/geojson-spec/#section-3.1.6
        return {
            type: "Polygon",
            coordinates: [
                [
                    [w, n],
                    [w, s],
                    [e, s],
                    [e, n],
                    [w, n]
                ]
            ]
        }
    }
}

function avg(...numbers: number[]) {
    return numbers.reduce((a, b) => a + b, 0) / numbers.length
}

function rad2Deg(radians: number) {
    return radians * (180 / Math.PI);
}

function deg2Rad(degrees: number) {
    return degrees * (Math.PI / 180);
}